import { produce } from "immer";
import { call, put, SagaGenerator, select, takeLatest } from "typed-redux-saga";

import { Activity, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { ApplicationActions } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { OverviewActivity } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/client-extensions";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";

import { createCustomNotificationAction } from "../../app/actions";
import { AssigneeCandidate } from "../../app/slices/users/types";
import { updateOverviewDocument } from "../../component/auxiliaryOverviewEngine/actions";
import { RESOURCE_KEYS } from "../../localization";
import { getDocument, modifyDocument } from "../../utilities/rpcHelper";

import { isWiedervorlageDocument } from "../wiedervorlagen/types";

import { assignWiedervorlageDocument } from "./actions";

const BASE_PATH = [
	{ elementName: "root", index: 1 },
	{ elementName: "assignee", index: 1 }
] as const;

const logger = LoggerFactory.getLogger("assignWiedervorlageSaga.ts");

export function* assignWiedervorlageSaga(): SagaGenerator<void> {
	return yield* takeLatest(assignWiedervorlageDocument, function* (action) {
		const { activityId, docRef, assignee } = action.payload;

		try {
			yield* put(ApplicationActions.setBusy(true));

			const document = (yield* call(getDocument, docRef))?.document;
			if (!isWiedervorlageDocument(document)) {
				yield* put(
					createCustomNotificationAction({
						title: { key: RESOURCE_KEYS.features.notification.title.error },
						message: { key: RESOURCE_KEYS.features.notification.error.assignWiedervorlage },
						icon: "error",
						severity: "error",
						activityId: action.payload.activityId,
						group: `claimWiedervorlageDocument:${docRef}`
					})
				);
				return;
			}

			yield* call(modifyDocument, docRef, setAssignee(document, assignee));

			const data = yield* select(ActivitySelectors.data(activityId));
			if (data && OverviewActivity.Data.DocumentListData.isInstance(data)) {
				const document = data.documents.find(it => it?.id === docRef);
				if (document) {
					yield* put(
						updateOverviewDocument({
							activityId,
							originalDocument: document,
							updates: assignee
								? [
										[[...BASE_PATH, { elementName: "id", index: 1 }], assignee.username],
										[[...BASE_PATH, { elementName: "firstName", index: 1 }], assignee.firstName],
										[[...BASE_PATH, { elementName: "lastName", index: 1 }], assignee.lastName],
										[[...BASE_PATH, { elementName: "fullName", index: 1 }], assignee.fullName]
									]
								: [[[...BASE_PATH], undefined]]
						})
					);
				}
			}
		} catch (error) {
			logger.error(error);
			yield* put(
				createCustomNotificationAction({
					title: { key: RESOURCE_KEYS.features.notification.title.error },
					message: { key: RESOURCE_KEYS.features.notification.error.assignWiedervorlage },
					icon: "error",
					severity: "error",
					activityId: action.payload.activityId,
					group: `claimWiedervorlageDocument:${docRef}`
				})
			);
		} finally {
			yield* put(ApplicationActions.setBusy(false));
		}
	});
}

function setAssignee<T extends Activity.Data.Document>(document: T, assignee: AssigneeCandidate | null | undefined): T {
	return produce(document, draft => {
		if (assignee) {
			draft.root.assignee ??= { id: assignee?.username };
			draft.root.assignee.id = assignee?.username;
			draft.root.assignee.firstName = assignee?.firstName;
			draft.root.assignee.lastName = assignee?.lastName;
			draft.root.assignee.fullName = assignee?.fullName;
		} else {
			delete draft.root.assignee;
		}
	});
}
